import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { colorTokens, themeColors } from '../theme/colors';

const STORAGE_KEY = 'link-generator-theme-v1';

const ThemeContext = createContext(null);

const applyThemeToDocument = (palette) => {
  const root = document.documentElement;

  colorTokens.forEach((token) => {
    root.style.setProperty(`--${token}`, palette[token]);
  });
};

const loadStoredTheme = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!parsed || !parsed.mode || !parsed.palettes) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export function ThemeProvider({ children }) {
  const stored = loadStoredTheme();
  const [mode, setMode] = useState(stored?.mode ?? 'light');
  const [palettes, setPalettes] = useState(stored?.palettes ?? themeColors);

  useEffect(() => {
    applyThemeToDocument(palettes[mode]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, palettes }));
  }, [mode, palettes]);

  const setColor = useCallback((token, value) => {
    setPalettes((previous) => ({
      ...previous,
      [mode]: {
        ...previous[mode],
        [token]: value
      }
    }));
  }, [mode]);

  const resetCurrentTheme = useCallback(() => {
    setPalettes((previous) => ({
      ...previous,
      [mode]: themeColors[mode]
    }));
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      palette: palettes[mode],
      toggleMode: () => setMode((previous) => (previous === 'light' ? 'dark' : 'light')),
      setColor,
      resetCurrentTheme
    }),
    [mode, palettes, resetCurrentTheme, setColor]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }
  return context;
}
